/**
 * extract is a shallow extraction utility
 *
 * single key:
 *  extract({ a: 1 }, 'a') -> <number> 1
 *  extract({ a: null }, 'a', 1) -> <number> 1
 *  extract({ a: 1 }, 'b') -> TypeError key does not exist
 *
 * mutli key:
 *  extract({ a: 1, b: '', c: true }, ['a', 'c']) -> <object> { a: 1, c: true }
 */
export function extract<T extends unknown, F extends keyof T>(source: T | undefined, field: F): T[F] | null;
export function extract<T extends unknown, F extends keyof T, D extends T[F]>(source: T | undefined, field: F, defaultValue: D): D;
export function extract<T extends unknown, F extends keyof T>(source: T | undefined, fields: F[]): { [K in F]: T[F] | null };
export function extract<T extends unknown, F extends keyof T>(source: T | undefined, fields: F | F[], defaultValue?: T[F]): T[F] | { [K in F]: T[F] | null } | null {
    const fieldsArr = Array.isArray(fields) ? fields : [fields];
    const response = fieldsArr.map(field => [field, source?.[field] ?? null])
        .reduce((obj, [k, v]) => {
            obj[k] = v;
            return obj;
        }, {} as any) as { [K in F]: T[F] | null };

    return fieldsArr?.length === 1 ?
        response[fieldsArr[0]] ?? defaultValue ?? null :
        response;
}

export function hasKey(obj: object, key: string, deep: boolean = false): boolean {
    let keyFound = false;

    if (obj !== undefined &&
        obj !== null &&
        typeof obj === 'object') {
        const keys = Object.keys(obj);
        keyFound = keys.includes(key);

        if (!keyFound && deep) {
            for (const _key of keys) {
                const nested = obj[_key];
                if (typeof nested === 'object') {
                    keyFound = hasKey(nested, key, deep);
                    if (keyFound) {
                        break;
                    }
                }
            }
        }
    }

    return keyFound;
}

function _findKeyPath(obj: object, key: string, deep: boolean = false, _path: string = ''): string | undefined {
    let keyFound = false;
    let path: string | undefined = _path;

    if (obj !== undefined &&
        obj !== null &&
        typeof obj === 'object') {
        const keys = Object.keys(obj);
        keyFound = keys.includes(key);

        if (keyFound) {
            path = `${path}.${key}`;
        } else if (deep) {
            for (const _key of keys) {
                const nested = obj[_key];
                if (typeof nested === 'object') {
                    const pathRgx = new RegExp(`\.${key}$`);
                    const nestedPath = _findKeyPath(nested, key, deep, `${path}.${_key}`);
                    if (!!nestedPath && pathRgx.test(nestedPath)) {
                        path = nestedPath;
                        break;
                    }
                }
            }
        }
    }

    return !path ? undefined : path.replace(/^\./, '');
}


export function findKeyPath(obj: object, key: string, deep: boolean = false) {
    return _findKeyPath(obj, key, deep);
}

export function isPromise(o: any): boolean {
    return typeof o === 'object' &&
        typeof o.then === 'function';
}

// Polyfill for Object.fromEntries
export function objectFromEntries(entries: [string | number, any][]) {
    let obj = {};
    for (let [key, value] of entries) {
        obj[key] = value;
    }
    return obj;
}
